import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle, ChevronDown, ChevronUp, ClipboardList, PenLine, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { FadeIn, GlassButton, GlassCard, GlassInput, GlassSelect } from '../../components/ui';

const API = '/erp/api/v1';

async function authHeaders() {
  const token = localStorage.getItem('nexus_token');
  if (!token) {
    window.location.assign('/erp/login');
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

async function jsonHeaders() {
  const token = localStorage.getItem('nexus_token');
  if (!token) {
    window.location.assign('/erp/login');
    return {};
  }
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const statusClass = (status) => {
  if (status === 'erro' || status === 'vencido') return 'border-red-400/25 bg-red-500/10 text-red-100';
  if (status === 'aguardando_contexto' || status === 'precisa_ocr') return 'border-amber-400/25 bg-amber-500/10 text-amber-100';
  if (status === 'aguardando_revisao' || status === 'revisao') return 'border-sky-400/25 bg-sky-500/10 text-sky-100';
  return 'border-white/10 bg-white/5 text-white/65';
};

const priorityClass = (priority) => {
  if (priority === 'alta') return 'text-red-200 bg-red-500/10 border-red-400/20';
  if (priority === 'baixa') return 'text-emerald-200 bg-emerald-500/10 border-emerald-400/20';
  return 'text-amber-100 bg-amber-500/10 border-amber-400/20';
};

const fmtDate = (value) => value ? new Date(value).toLocaleString('pt-BR') : '-';

export const RevisoesPendentes = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [originSummary, setOriginSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('todos');
  const [originFilter, setOriginFilter] = useState('todos');
  const [expandedId, setExpandedId] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/documentos/revisoes`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || 'Erro ao carregar revisões.');
      setItems(data.items || []);
      setSummary(data.por_modulo || {});
      setOriginSummary(data.por_origem || {});
    } catch (err) {
      setError(err.message || 'Erro ao carregar revisões.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const moduleOptions = useMemo(() => [
    { value: 'todos', label: 'Todos os módulos' },
    ...Object.keys(summary).sort().map((name) => ({ value: name, label: name })),
  ], [summary]);

  const originOptions = useMemo(() => [
    { value: 'todos', label: 'Todas as origens' },
    ...Object.keys(originSummary).sort().map((name) => ({ value: name, label: name })),
  ], [originSummary]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      const byModule = moduleFilter === 'todos' || item.modulo === moduleFilter;
      const byOrigin = originFilter === 'todos' || item.origem === originFilter;
      if (!byModule || !byOrigin) return false;
      if (!term) return true;
      return [item.modulo, item.origem, item.tipo, item.titulo, item.subtitulo, item.status]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [items, moduleFilter, originFilter, search]);

  const highPriority = items.filter((item) => item.prioridade === 'alta').length;

  const completeOcr = (item) => {
    const params = new URLSearchParams({
      documento_id: item.id,
      modulo: item.modulo || '',
      tipo: item.tipo || '',
      acao: 'encaminhar',
    });
    navigate(`/documentos?${params.toString()}`);
  };

  const markReviewed = async (item) => {
    const key = `${item.action_type}-${item.id}`;
    setActionLoading(key);
    setError('');
    try {
      const headers = await jsonHeaders();
      const res = await fetch(`${API}/documentos/revisoes/${item.action_type}/${item.id}/revisado`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ observacao: 'Revisado pela fila unificada.' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || 'Erro ao marcar como revisado.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Erro ao marcar como revisado.');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <FadeIn className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Central de Revisões</h1>
          <p className="mt-1 text-sm text-white/50">Documentos, vínculos e divergências que precisam de conferência humana antes de orientar os demais módulos.</p>
        </div>
        <GlassButton variant="secondary" icon={RefreshCw} onClick={loadData} loading={loading}>Atualizar</GlassButton>
      </div>

      {error && (
        <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <GlassCard className="p-5">
          <p className="text-sm text-white/45">Total em revisão</p>
          <p className="mt-2 text-3xl font-semibold text-white">{items.length}</p>
        </GlassCard>
        <GlassCard className="p-5">
          <p className="text-sm text-white/45">Prioridade alta</p>
          <p className="mt-2 text-3xl font-semibold text-red-100">{highPriority}</p>
        </GlassCard>
        <GlassCard className="p-5">
          <p className="text-sm text-white/45">Módulos envolvidos</p>
          <p className="mt-2 text-3xl font-semibold text-white">{Object.keys(summary).length}</p>
        </GlassCard>
        <GlassCard className="p-5">
          <p className="text-sm text-white/45">Integridade de dados</p>
          <p className="mt-2 text-3xl font-semibold text-violet-200">{originSummary.Integridade || 0}</p>
        </GlassCard>
      </div>

      {(originSummary.Integridade || 0) > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-violet-400/20 bg-violet-500/10 px-4 py-3 text-sm text-violet-100">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          <p>Itens de integridade não são baixados por este painel: abra o cadastro indicado, corrija a origem e volte aqui para conferir se o alerta desapareceu.</p>
        </div>
      )}

      <GlassCard className="p-5">
        <div className="mb-4 grid gap-3 lg:grid-cols-[1fr_220px_220px]">
          <GlassInput
            icon={Search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por módulo, tipo, arquivo, status"
          />
          <GlassSelect
            value={moduleFilter}
            onChange={(event) => setModuleFilter(event.target.value)}
            options={moduleOptions}
            placeholder=""
          />
          <GlassSelect value={originFilter} onChange={(event) => setOriginFilter(event.target.value)} options={originOptions} placeholder="" />
        </div>

        <div className="overflow-hidden rounded-xl border border-white/10">
          <div className="divide-y divide-white/10 md:hidden">
            {filtered.map((item) => {
              const key = `${item.origem}-${item.id}`;
              const expanded = expandedId === key;
              const trail = item.trilha || {};
              return (
                <article key={key} className="space-y-3 bg-white/[0.015] p-4">
                  <div className="flex items-start gap-3">
                    <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-sky-300" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white/90">{item.titulo}</p>
                      <p className="mt-1 text-xs text-white/50">{item.modulo} / {item.origem} · {fmtDate(item.created_at)}</p>
                    </div>
                    <span className={`shrink-0 rounded-full border px-2 py-1 text-[11px] ${priorityClass(item.prioridade)}`}>{item.prioridade}</span>
                  </div>
                  <p className="text-sm text-white/70">{item.motivo || item.subtitulo || 'Conferir campos e documento de origem.'}</p>
                  {item.impacto && <p className="text-xs text-amber-200/80">{item.impacto}</p>}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-1 text-xs ${statusClass(item.status)}`}>{item.status}</span>
                    {item.can_mark_reviewed !== false && (
                      <GlassButton type="button" size="sm" variant="outline" icon={CheckCircle} onClick={() => markReviewed(item)} loading={actionLoading === `${item.action_type}-${item.id}`}>Revisado</GlassButton>
                    )}
                    {item.action_type === 'ocr' && <GlassButton type="button" size="sm" variant="outline" icon={PenLine} onClick={() => completeOcr(item)}>Completar</GlassButton>}
                    <GlassButton type="button" size="sm" variant="outline" icon={expanded ? ChevronUp : ChevronDown} onClick={() => setExpandedId(expanded ? '' : key)}>Trilha</GlassButton>
                    <GlassButton type="button" size="sm" variant="outline" icon={ArrowRight} onClick={() => navigate(item.rota || '/documentos')}>Abrir</GlassButton>
                  </div>
                  {expanded && (
                    <div className="grid gap-3 rounded-lg border border-sky-400/15 bg-sky-500/[0.035] p-3 text-xs">
                      <div><p className="text-white/40">Registro afetado</p><p className="mt-1 text-white/80">{trail.registro_afetado || item.titulo}</p></div>
                      <div><p className="text-white/40">Vínculo a conferir</p><p className="mt-1 text-amber-100">{trail.vinculo_a_conferir || item.motivo}</p></div>
                      <div><p className="text-white/40">Próxima ação</p><p className="mt-1 text-sky-100">{trail.acao_recomendada || item.impacto || 'Abra o cadastro indicado e confira a origem.'}</p></div>
                    </div>
                  )}
                </article>
              );
            })}
            {!loading && filtered.length === 0 && <p className="px-4 py-10 text-center text-sm text-white/45">Nenhuma revisão pendente encontrada.</p>}
          </div>
          <div className="hidden max-h-[680px] overflow-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-950/90 text-xs uppercase tracking-wide text-white/45">
                <tr>
                  <th className="px-4 py-3">Item</th>
                  <th className="px-4 py-3">Origem</th>
                  <th className="px-4 py-3">Motivo</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Prioridade</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filtered.map((item) => {
                  const key = `${item.origem}-${item.id}`;
                  const expanded = expandedId === key;
                  const trail = item.trilha || {};
                  return <React.Fragment key={key}>
                  <tr className="bg-white/[0.015] hover:bg-white/[0.04]">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <ClipboardList className="mt-0.5 h-4 w-4 text-sky-300" />
                        <div>
                          <p className="font-medium text-white/85">{item.titulo}</p>
                          <p className="mt-1 line-clamp-2 max-w-xl text-xs text-white/45">{item.subtitulo || item.tipo}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/65">{item.modulo} / {item.origem}</td>
                    <td className="px-4 py-3">
                      <p className="max-w-xs text-xs text-white/65">{item.motivo || 'Conferir campos e documento de origem.'}</p>
                      {item.impacto && <p className="mt-1 max-w-xs text-xs text-amber-200/80">{item.impacto}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-2 py-1 text-xs ${statusClass(item.status)}`}>{item.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs ${priorityClass(item.prioridade)}`}>
                        {item.prioridade === 'alta' && <AlertTriangle className="h-3 w-3" />}
                        {item.prioridade}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/50">{fmtDate(item.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {item.can_mark_reviewed !== false && (
                          <GlassButton
                            type="button"
                            size="sm"
                            variant="outline"
                            icon={CheckCircle}
                            onClick={() => markReviewed(item)}
                            loading={actionLoading === `${item.action_type}-${item.id}`}
                          >
                            Revisado
                          </GlassButton>
                        )}
                        {item.action_type === 'ocr' && (
                          <GlassButton type="button" size="sm" variant="outline" icon={PenLine} onClick={() => completeOcr(item)}>Completar</GlassButton>
                        )}
                        <GlassButton type="button" size="sm" variant="outline" icon={expanded ? ChevronUp : ChevronDown} onClick={() => setExpandedId(expanded ? '' : key)}>Trilha</GlassButton>
                        <GlassButton type="button" size="sm" variant="outline" icon={ArrowRight} onClick={() => navigate(item.rota || '/documentos')}>Abrir</GlassButton>
                      </div>
                    </td>
                  </tr>
                  {expanded && <tr className="bg-sky-500/[0.035]">
                    <td colSpan="7" className="px-5 py-4">
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5 text-xs">
                        <div><p className="text-white/40">Origem</p><p className="mt-1 text-white/80">{trail.origem || item.origem}</p></div>
                        <div><p className="text-white/40">Registro afetado</p><p className="mt-1 text-white/80">{trail.registro_afetado || item.titulo}</p></div>
                        <div><p className="text-white/40">Vínculo a conferir</p><p className="mt-1 text-amber-100">{trail.vinculo_a_conferir || item.motivo}</p></div>
                        <div><p className="text-white/40">Módulo de destino</p><p className="mt-1 text-white/80">{trail.modulo_destino || item.modulo}</p></div>
                        <div><p className="text-white/40">Próxima ação</p><p className="mt-1 text-sky-100">{trail.acao_recomendada || item.impacto}</p></div>
                      </div>
                    </td>
                  </tr>}
                  </React.Fragment>;
                })}
                ))}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-10 text-center text-white/45">Nenhuma revisão pendente encontrada.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </GlassCard>
    </FadeIn>
  );
};

export default RevisoesPendentes;
